import React from "react";

import { BranchAccountDto } from "services/organizationBranches/restModels";
import { formatDate } from "shared/lib/dateTime";
import { formatPhoneNumber } from "shared/lib/phoneNumber";
import { Banner } from "shared/ui/Banner";
import { Button } from "shared/ui/Button";
import { DataTable, DataTableColumn } from "shared/ui/DataTable";
import { EmptyState } from "shared/ui/EmptyState";
import { DeleteIcon, EditIcon } from "shared/ui/icons";
import { IconButton } from "shared/ui/IconButton";
import { Skeleton } from "shared/ui/Skeleton";

import { MutedLine, RowActions, TableActions } from "../../styles";

interface AccountsTabProps {
    accounts: BranchAccountDto[];
    loading: boolean;
    error: boolean;
    saving: boolean;
    onAdd: () => void;
    onEdit: (account: BranchAccountDto) => void;
    onDelete: (account: BranchAccountDto) => void;
    onRetry: () => void;
}

export const AccountsTab = ({
    accounts,
    loading,
    error,
    saving,
    onAdd,
    onEdit,
    onDelete,
    onRetry,
}: AccountsTabProps) => {
    if (error) {
        return (
            <Banner tone="danger" title="Не удалось загрузить аккаунты">
                <Button size="small" variant="outlined" color="neutral" onClick={onRetry}>
                    Повторить
                </Button>
            </Banner>
        );
    }

    if (loading) return <Skeleton height="240px" />;

    if (accounts.length === 0) {
        return (
            <EmptyState
                title="У заведения нет аккаунтов"
                description="Без аккаунта в кабинет заведения никто не войдёт."
                action={
                    <Button onClick={onAdd} disabled={saving}>
                        Добавить аккаунт
                    </Button>
                }
            />
        );
    }

    const columns: DataTableColumn<BranchAccountDto>[] = [
        {
            key: "phoneNumber",
            title: "Номер телефона",
            role: "primary",
            render: (account) => (
                <>
                    <div>{formatPhoneNumber(account.phoneNumber)}</div>
                    <MutedLine>ID {account.id}</MutedLine>
                </>
            ),
        },
        {
            key: "role",
            title: "Роль",
            role: "secondary",
            width: 140,
            render: (account) => (account.isOwner ? "Владелец" : "—"),
        },
        {
            key: "createdAt",
            title: "Создан",
            role: "secondary",
            width: 120,
            render: (account) => formatDate(account.createdAt),
        },
        {
            key: "actions",
            title: "Действия",
            role: "action",
            width: 100,
            render: (account) => (
                <RowActions>
                    <IconButton
                        label="Изменить номер"
                        onClick={() => onEdit(account)}
                        disabled={saving}
                    >
                        <EditIcon />
                    </IconButton>

                    {/* Единственный аккаунт удалить нельзя — бэк это тоже не даст, кнопку не показываем. */}
                    {accounts.length > 1 && (
                        <IconButton
                            label="Удалить аккаунт"
                            tone="danger"
                            onClick={() => onDelete(account)}
                            disabled={saving}
                        >
                            <DeleteIcon />
                        </IconButton>
                    )}
                </RowActions>
            ),
        },
    ];

    return (
        <>
            <DataTable columns={columns} rows={accounts} rowKey={(account) => account.id} />

            <TableActions>
                <Button onClick={onAdd} disabled={saving}>
                    Добавить аккаунт
                </Button>
            </TableActions>
        </>
    );
};
